'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react'
import { cn } from '@/lib/utils'
import './hero-engine.css'

/**
 * The hero's right column: the Pixelmojo engine. Radar, Vector and Hive sit
 * on one isometric circuit board whose routes return to a human control,
 * ported from the design mock `public/pixelmojo-hero-mouse.html`
 * (2026-10-03; untracked on purpose). It replaced the five-phase system
 * trace on 2026-10-04, which is kept in `archive/HeroSystemTrace.tsx`.
 *
 * The composition and palette come from the mock. The finishing pass aligns
 * the arrow and check to the board's projection; hover follows the mock.
 * Every class, id and custom property
 * is prefixed `hpe-`: the SVG's gradient, filter and mask ids are global to
 * the document, and another page already defines `scan`. Styling, including
 * the SVG's light palette, lives in `hero-engine.css`.
 *
 * Removed at Lloyd's request (2026-10-04): the mock's topline ("The
 * Pixelmojo engine" + Replay), its caption under the SVG (so the modules no
 * longer point `aria-controls` at it), and the blurred floor shadow under
 * the board with its filter (muddy on the light theme, near-invisible on
 * dark). Without them the art is just the SVG.
 *
 * The mock's inline script is this component's state, event for event:
 *   - `focus` is the mock's `data-focus`. A focused module dims the other
 *     two. Their labels stay at full strength (the mock faded them to 0.32,
 *     under 4.5:1 contrast in both themes).
 *   - `interacting` runs the travelling signals and the radar sweep while a
 *     mouse or pen is over the engine or a module has keyboard focus.
 *   - `intro` is not in the mock: it plays that same state once when the
 *     entrance lands, so touch visitors, who never hover, see it run too.
 *   - The parallax writes `--hpe-pointer-x/y` straight onto the root, one
 *     animation frame per move as in the mock, so tracking the pointer never
 *     re-renders the SVG.
 * Touch never hovers: each module is a link to its product page.
 * Reduced motion keeps selection but never animates or moves. Leaving the
 * window or hiding the tab resets everything.
 */

type Focus = 'all' | 'radar' | 'vector' | 'hive'
type Product = Exclude<Focus, 'all'>

const PRODUCTS: readonly Product[] = ['radar', 'vector', 'hive']

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

const prefersReducedMotion = () => window.matchMedia(REDUCED_MOTION).matches

/** The load pass starts as HomeHero's entrance lands: `hh-visual-in` in
 *  home-hero.css runs 1200ms after a 120ms delay. */
const INTRO_DELAY_MS = 1320
/** One pass: the return signal starts last (2.1s) and runs 2.8s, the
 *  `hpe-travel` timings in hero-engine.css. */
const INTRO_MS = 4900

export default function HeroEngine() {
  const [focus, setFocus] = useState<Focus>('all')
  const [interacting, setInteracting] = useState(false)
  const [intro, setIntro] = useState(false)
  const artRef = useRef<HTMLDivElement>(null)
  const engineRef = useRef<SVGSVGElement>(null)
  /** A mouse or pen is over the engine (the mock's `hovering`). */
  const hovering = useRef(false)
  /** The module under the mouse, if any (the mock's `pointerModule`). */
  const pointerModule = useRef<Product | null>(null)
  const frame = useRef(0)
  /** The load pass's start and end timers, owned by `clearIntro`. */
  const introTimers = useRef<number[]>([])

  /** Hover and keyboard focus decide the state (the mock's `sync`). */
  const sync = useCallback(() => {
    const focused = engineRef.current?.querySelector<SVGAElement>(
      '[data-module]:focus-visible'
    )
    const keyboardModule = PRODUCTS.find(
      product => product === focused?.dataset.module
    )
    setInteracting(
      !prefersReducedMotion() && (hovering.current || Boolean(keyboardModule))
    )
    setFocus(pointerModule.current ?? keyboardModule ?? 'all')
  }, [])

  const resetPosition = useCallback(() => {
    cancelAnimationFrame(frame.current)
    frame.current = 0
    artRef.current?.style.removeProperty('--hpe-pointer-x')
    artRef.current?.style.removeProperty('--hpe-pointer-y')
  }, [])

  /** Ends the load pass and cancels its pending timers, so a reset during
   *  the entrance delay can never switch the motion back on. */
  const clearIntro = useCallback(() => {
    introTimers.current.forEach(id => window.clearTimeout(id))
    introTimers.current = []
    setIntro(false)
  }, [])

  /** Back to rest: no hover, motion or parallax (the mock's `stop`). */
  const stop = useCallback(() => {
    hovering.current = false
    pointerModule.current = null
    resetPosition()
    clearIntro()
    setInteracting(false)
    setFocus('all')
  }, [clearIntro, resetPosition])

  useEffect(() => {
    if (prefersReducedMotion()) return
    introTimers.current = [
      window.setTimeout(() => {
        if (!document.hidden) setIntro(true)
      }, INTRO_DELAY_MS),
      window.setTimeout(clearIntro, INTRO_DELAY_MS + INTRO_MS),
    ]
    return clearIntro
  }, [clearIntro])

  useEffect(() => {
    const reducedMotion = window.matchMedia(REDUCED_MOTION)
    const onMotionChange = () => {
      resetPosition()
      sync()
    }
    const onVisibilityChange = () => {
      if (document.hidden) stop()
    }
    reducedMotion.addEventListener('change', onMotionChange)
    window.addEventListener('blur', stop)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      reducedMotion.removeEventListener('change', onMotionChange)
      window.removeEventListener('blur', stop)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      resetPosition()
    }
  }, [resetPosition, stop, sync])

  const onEnginePointerEnter = (event: PointerEvent<SVGSVGElement>) => {
    if (event.pointerType === 'touch') return
    hovering.current = true
    sync()
  }

  const onEnginePointerMove = (event: PointerEvent<SVGSVGElement>) => {
    if (event.pointerType === 'touch' || prefersReducedMotion()) return
    hovering.current = true
    setInteracting(true)
    cancelAnimationFrame(frame.current)
    const { clientX, clientY, currentTarget } = event
    frame.current = requestAnimationFrame(() => {
      frame.current = 0
      const rect = currentTarget.getBoundingClientRect()
      const x = Math.max(
        -1,
        Math.min(1, ((clientX - rect.left) / rect.width) * 2 - 1)
      )
      const y = Math.max(
        -1,
        Math.min(1, ((clientY - rect.top) / rect.height) * 2 - 1)
      )
      artRef.current?.style.setProperty('--hpe-pointer-x', `${x * 7}px`)
      artRef.current?.style.setProperty('--hpe-pointer-y', `${y * 5}px`)
    })
  }

  const onEnginePointerLeave = () => {
    hovering.current = false
    pointerModule.current = null
    resetPosition()
    sync()
  }

  /** A click on the board, not on a module, shows all three again. */
  const onEngineClick = (event: MouseEvent<SVGSVGElement>) => {
    if (
      event.target instanceof Element &&
      event.target.closest('[data-module]')
    )
      return
    setFocus('all')
  }

  /** Focus leaving a module settles after the next one takes it. */
  const onEngineBlur = () => queueMicrotask(sync)

  const moduleEvents = (product: Product) => ({
    onPointerEnter: (event: PointerEvent<Element>) => {
      if (event.pointerType === 'touch') return
      pointerModule.current = product
      sync()
    },
    onPointerLeave: () => {
      pointerModule.current = null
      sync()
    },
    onFocus: () => {
      setFocus(product)
      sync()
    },
    onKeyDown: (event: KeyboardEvent<SVGElement | HTMLAnchorElement>) => {
      if (event.key === 'Escape') {
        event.currentTarget.blur()
        pointerModule.current = null
        sync()
      }
    },
  })

  return (
    <div
      ref={artRef}
      className={cn(
        'hpe-hero-art',
        intro && 'hpe-intro',
        interacting && 'hpe-interacting'
      )}
      data-focus={focus}
    >
      <svg
        ref={engineRef}
        onPointerEnter={onEnginePointerEnter}
        onPointerMove={onEnginePointerMove}
        onPointerLeave={onEnginePointerLeave}
        onPointerCancel={stop}
        onClick={onEngineClick}
        onBlur={onEngineBlur}
        className='hpe-engine'
        viewBox='0 0 680 590'
        xmlns='http://www.w3.org/2000/svg'
        role='group'
        aria-labelledby='hpe-engine-title'
        aria-describedby='hpe-engine-desc'
      >
        <title id='hpe-engine-title'>
          The Pixelmojo engine: from signal to a system you own
        </title>
        <desc id='hpe-engine-desc'>
          Three dimensional modules share one circuit board. Radar is a blue
          scanning disc. Vector is a pink directional arrow. Hive is a golden
          cluster of cells. Their connected routes return to a human control
          marked with a check. Move your mouse over the engine to animate it.
          Hover or focus a module to explore it. Click or tap a module to visit
          its product page.
        </desc>
        <defs>
          <linearGradient id='hpe-surface' x1='0' y1='0' x2='1' y2='1'>
            <stop stopColor='#303646' />
            <stop offset='.42' stopColor='#1B202D' />
            <stop offset='1' stopColor='#10141F' />
          </linearGradient>
          <linearGradient id='hpe-board' x1='0' y1='0' x2='0.8' y2='1'>
            <stop stopColor='#171B26' />
            <stop offset='.6' stopColor='#121722' />
            <stop offset='1' stopColor='#202531' />
          </linearGradient>
          <linearGradient id='hpe-blueGlass' x1='0' y1='0' x2='1' y2='1'>
            <stop stopColor='#94DDFF' />
            <stop offset='.45' stopColor='#53B9EE' />
            <stop offset='1' stopColor='#297CAA' />
          </linearGradient>
          <linearGradient id='hpe-pinkGlass' x1='0' y1='0' x2='1' y2='1'>
            <stop stopColor='#FF95C6' />
            <stop offset='.55' stopColor='#EE398E' />
            <stop offset='1' stopColor='#B9165F' />
          </linearGradient>
          <linearGradient id='hpe-goldGlass' x1='0' y1='0' x2='1' y2='1'>
            <stop stopColor='#FFEDA9' />
            <stop offset='.45' stopColor='#F9CA45' />
            <stop offset='1' stopColor='#DDA41F' />
          </linearGradient>
          <radialGradient id='hpe-disc'>
            <stop stopColor='#1C3A50' />
            <stop offset='1' stopColor='#111E2C' />
          </radialGradient>
          <linearGradient id='hpe-scan'>
            <stop stopColor='#51B7EB' stopOpacity='0' />
            <stop offset='1' stopColor='#51B7EB' stopOpacity='.55' />
          </linearGradient>
          <filter id='hpe-glow' x='-80%' y='-80%' width='260%' height='260%'>
            <feGaussianBlur stdDeviation='3' />
          </filter>
          <pattern
            id='hpe-dots'
            width='24'
            height='24'
            patternUnits='userSpaceOnUse'
          >
            <circle cx='1' cy='1' r='.8' fill='#333C4B' />
          </pattern>
          <radialGradient id='hpe-fade'>
            <stop offset='.3' stopColor='white' />
            <stop offset='1' stopColor='black' />
          </radialGradient>
          <mask id='hpe-dotfade'>
            <rect width='680' height='590' fill='url(#hpe-fade)' />
          </mask>
        </defs>
        <g className='hpe-engine-scene'>
          <rect
            x='8'
            y='40'
            width='664'
            height='510'
            fill='url(#hpe-dots)'
            opacity='.46'
            mask='url(#hpe-dotfade)'
          />
          <g className='hpe-assembly'>
            <g className='hpe-motherboard'>
              <polygon
                points='56.20,332.10 340.00,487.20 340.00,504.20 56.20,349.10'
                fill='#0D1019'
                stroke='#363F50'
              />
              <polygon
                points='623.80,332.10 340.00,487.20 340.00,504.20 623.80,349.10'
                fill='#161B27'
                stroke='#363F50'
              />
              <polygon
                points='340.00,177.00 623.80,332.10 340.00,487.20 56.20,332.10'
                fill='url(#hpe-board)'
                stroke='#363F50'
              />
            </g>
            <path
              d='M56.20 346.10 L340.00 501.20 L623.80 346.10'
              fill='none'
              stroke='#4B5568'
              strokeWidth='0.7'
            />
            <path
              d='M334.84 195.74 L587.68 333.92'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <path
              d='M345.16 195.74 L92.32 333.92'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <path
              d='M313.34 207.49 L566.18 345.67'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <path
              d='M366.66 207.49 L113.82 345.67'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <path
              d='M114.68 316.06 L367.52 454.24'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <path
              d='M565.32 316.06 L312.48 454.24'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <path
              d='M93.18 327.81 L346.02 465.99'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <path
              d='M586.82 327.81 L333.98 465.99'
              fill='none'
              stroke='#333D4E'
              strokeWidth='0.65'
              opacity='.6'
            />
            <ellipse
              cx='340.0'
              cy='189.1'
              rx='3'
              ry='1.6'
              fill='#080C12'
              stroke='#556075'
              strokeWidth='.8'
            />
            <ellipse
              cx='598.0'
              cy='330.1'
              rx='3'
              ry='1.6'
              fill='#080C12'
              stroke='#556075'
              strokeWidth='.8'
            />
            <ellipse
              cx='82.0'
              cy='330.1'
              rx='3'
              ry='1.6'
              fill='#080C12'
              stroke='#556075'
              strokeWidth='.8'
            />
            <ellipse
              cx='340.0'
              cy='471.09999999999997'
              rx='3'
              ry='1.6'
              fill='#080C12'
              stroke='#556075'
              strokeWidth='.8'
            />
            <path
              d='M216.16 322.58 L273.78 291.09 L340.00 254.90'
              fill='none'
              stroke='#050911'
              strokeWidth='9'
              strokeLinejoin='round'
            />
            <path
              d='M216.16 322.58 L273.78 291.09 L340.00 254.90'
              fill='none'
              stroke='#51B7EB'
              strokeWidth='2.5'
              className='hpe-signal-base hpe-signal-radar'
              opacity='.62'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M216.16 322.58 L273.78 291.09 L340.00 254.90'
              fill='none'
              stroke='#51B7EB'
              strokeWidth='6'
              className='hpe-signal-glow hpe-signal-radar'
              opacity='.22'
              filter='url(#hpe-glow)'
            />
            <path
              d='M216.16 322.58 L273.78 291.09 L340.00 254.90'
              fill='none'
              stroke='#FFFFFF'
              strokeWidth='2.5'
              className='hpe-traveller hpe-traveller-radar'
              pathLength='100'
              strokeDasharray='9 100'
              strokeDashoffset='110'
              strokeLinecap='round'
            />
            <path
              d='M340.00 254.90 L405.36 290.62 L463.84 322.58'
              fill='none'
              stroke='#050911'
              strokeWidth='9'
              strokeLinejoin='round'
            />
            <path
              d='M340.00 254.90 L405.36 290.62 L463.84 322.58'
              fill='none'
              stroke='#EC368C'
              strokeWidth='2.5'
              className='hpe-signal-base hpe-signal-vector'
              opacity='.62'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M340.00 254.90 L405.36 290.62 L463.84 322.58'
              fill='none'
              stroke='#EC368C'
              strokeWidth='6'
              className='hpe-signal-glow hpe-signal-vector'
              opacity='.22'
              filter='url(#hpe-glow)'
            />
            <path
              d='M340.00 254.90 L405.36 290.62 L463.84 322.58'
              fill='none'
              stroke='#FFFFFF'
              strokeWidth='2.5'
              className='hpe-traveller hpe-traveller-vector'
              pathLength='100'
              strokeDasharray='9 100'
              strokeDashoffset='110'
              strokeLinecap='round'
            />
            <path
              d='M469.00 325.40 L404.50 360.65 L340.00 395.90'
              fill='none'
              stroke='#050911'
              strokeWidth='9'
              strokeLinejoin='round'
            />
            <path
              d='M469.00 325.40 L404.50 360.65 L340.00 395.90'
              fill='none'
              stroke='#F9CA45'
              strokeWidth='2.5'
              className='hpe-signal-base hpe-signal-hive'
              opacity='.62'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M469.00 325.40 L404.50 360.65 L340.00 395.90'
              fill='none'
              stroke='#F9CA45'
              strokeWidth='6'
              className='hpe-signal-glow hpe-signal-hive'
              opacity='.22'
              filter='url(#hpe-glow)'
            />
            <path
              d='M469.00 325.40 L404.50 360.65 L340.00 395.90'
              fill='none'
              stroke='#FFFFFF'
              strokeWidth='2.5'
              className='hpe-traveller hpe-traveller-hive'
              pathLength='100'
              strokeDasharray='9 100'
              strokeDashoffset='110'
              strokeLinecap='round'
            />
            <path
              d='M340.00 395.90 L279.80 363.00 L211.00 325.40'
              fill='none'
              stroke='#050911'
              strokeWidth='9'
              strokeLinejoin='round'
            />
            <path
              d='M340.00 395.90 L279.80 363.00 L211.00 325.40'
              fill='none'
              stroke='#8893A7'
              strokeWidth='2.5'
              className='hpe-signal-base hpe-signal-return'
              opacity='.62'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M340.00 395.90 L279.80 363.00 L211.00 325.40'
              fill='none'
              stroke='#8893A7'
              strokeWidth='6'
              className='hpe-signal-glow hpe-signal-return'
              opacity='.22'
              filter='url(#hpe-glow)'
            />
            <path
              d='M340.00 395.90 L279.80 363.00 L211.00 325.40'
              fill='none'
              stroke='#FFFFFF'
              strokeWidth='2.5'
              className='hpe-traveller hpe-traveller-return'
              pathLength='100'
              strokeDasharray='9 100'
              strokeDashoffset='110'
              strokeLinecap='round'
            />
            <g transform='matrix(.86 .47 -.86 .47 276.36 289.68)'>
              <rect x='-4' y='-4' width='8' height='8' rx='1' fill='#C1CCD9' />
              <rect x='-1.5' y='-1.5' width='3' height='3' fill='#202736' />
            </g>
            <g transform='matrix(.86 .47 -.86 .47 404.50 290.15)'>
              <rect x='-4' y='-4' width='8' height='8' rx='1' fill='#C1CCD9' />
              <rect x='-1.5' y='-1.5' width='3' height='3' fill='#202736' />
            </g>
            <g transform='matrix(.86 .47 -.86 .47 403.64 361.12)'>
              <rect x='-4' y='-4' width='8' height='8' rx='1' fill='#C1CCD9' />
              <rect x='-1.5' y='-1.5' width='3' height='3' fill='#202736' />
            </g>
            <g transform='matrix(.86 .47 -.86 .47 276.36 361.12)'>
              <rect x='-4' y='-4' width='8' height='8' rx='1' fill='#C1CCD9' />
              <rect x='-1.5' y='-1.5' width='3' height='3' fill='#202736' />
            </g>
            <a
              className='hpe-module hpe-module-vector'
              data-module='vector'
              {...moduleEvents('vector')}
              href='/vector'
              tabIndex={0}
              aria-label='Explore Vector'
            >
              <g>
                <polygon
                  points='243.68,246.90 340.00,299.54 340.00,307.54 243.68,254.90'
                  fill='#10141E'
                  stroke='#384154'
                />
                <polygon
                  points='436.32,246.90 340.00,299.54 340.00,307.54 436.32,254.90'
                  fill='#1C2330'
                  stroke='#384154'
                />
                <polygon
                  points='340.00,194.26 436.32,246.90 340.00,299.54 243.68,246.90'
                  fill='#131925'
                  stroke='#384154'
                />
              </g>
              <g>
                <polygon
                  points='254.00,220.90 340.00,267.90 340.00,291.90 254.00,244.90'
                  fill='#191F2C'
                  stroke='#526078'
                />
                <polygon
                  points='426.00,220.90 340.00,267.90 340.00,291.90 426.00,244.90'
                  fill='#252C3C'
                  stroke='#526078'
                />
                <polygon
                  points='340.00,173.90 426.00,220.90 340.00,267.90 254.00,220.90'
                  fill='url(#hpe-surface)'
                  stroke='#526078'
                />
              </g>
              <path
                d='M254.00 239.90 L340.00 286.90 L426.00 239.90'
                fill='none'
                stroke='#EC368C'
                strokeWidth='1.5'
                opacity='.85'
              />
              <path
                d='M408.80 246.30 L408.80 238.30'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M401.92 250.06 L401.92 242.06'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M395.04 253.82 L395.04 245.82'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M388.16 257.58 L388.16 249.58'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M381.28 261.34 L381.28 253.34'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M374.40 265.10 L374.40 257.10'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M367.52 268.86 L367.52 260.86'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M360.64 272.62 L360.64 264.62'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M272.92 248.24 L268.62 250.59'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M280.66 252.47 L276.36 254.82'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M288.40 256.70 L284.10 259.05'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M296.14 260.93 L291.84 263.28'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M303.88 265.16 L299.58 267.51'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M311.62 269.39 L307.32 271.74'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M319.36 273.62 L315.06 275.97'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <g className='hpe-vector-arrow'>
                <polygon
                  points='332.26,204.11 297.86,185.31 297.86,199.31 332.26,218.11'
                  fill='#8D204F'
                  stroke='#CF3978'
                  strokeWidth='.7'
                />
                <polygon
                  points='376.12,217.74 315.92,213.04 315.92,227.04 376.12,231.74'
                  fill='#8D204F'
                  stroke='#CF3978'
                  strokeWidth='.7'
                />
                <polygon
                  points='316.78,174.97 351.18,193.77 367.52,184.84 376.12,217.74 315.92,213.04 332.26,204.11 297.86,185.31'
                  fill='url(#hpe-pinkGlass)'
                  stroke='#FFA4CC'
                  strokeWidth='.9'
                />
              </g>
            </a>
            <a
              className='hpe-module hpe-module-radar'
              data-module='radar'
              {...moduleEvents('radar')}
              href='/platform'
              tabIndex={0}
              aria-label='Explore Radar'
            >
              <g>
                <polygon
                  points='114.68,317.40 211.00,370.04 211.00,378.04 114.68,325.40'
                  fill='#10141E'
                  stroke='#384154'
                />
                <polygon
                  points='307.32,317.40 211.00,370.04 211.00,378.04 307.32,325.40'
                  fill='#1C2330'
                  stroke='#384154'
                />
                <polygon
                  points='211.00,264.76 307.32,317.40 211.00,370.04 114.68,317.40'
                  fill='#131925'
                  stroke='#384154'
                />
              </g>
              <g>
                <polygon
                  points='125.00,291.40 211.00,338.40 211.00,362.40 125.00,315.40'
                  fill='#191F2C'
                  stroke='#526078'
                />
                <polygon
                  points='297.00,291.40 211.00,338.40 211.00,362.40 297.00,315.40'
                  fill='#252C3C'
                  stroke='#526078'
                />
                <polygon
                  points='211.00,244.40 297.00,291.40 211.00,338.40 125.00,291.40'
                  fill='url(#hpe-surface)'
                  stroke='#526078'
                />
              </g>
              <path
                d='M125.00 310.40 L211.00 357.40 L297.00 310.40'
                fill='none'
                stroke='#51B7EB'
                strokeWidth='1.5'
                opacity='.85'
              />
              <path
                d='M279.80 316.80 L279.80 308.80'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M272.92 320.56 L272.92 312.56'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M266.04 324.32 L266.04 316.32'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M259.16 328.08 L259.16 320.08'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M252.28 331.84 L252.28 323.84'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M245.40 335.60 L245.40 327.60'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M238.52 339.36 L238.52 331.36'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M231.64 343.12 L231.64 335.12'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M143.92 318.74 L139.62 321.09'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M151.66 322.97 L147.36 325.32'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M159.40 327.20 L155.10 329.55'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M167.14 331.43 L162.84 333.78'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M174.88 335.66 L170.58 338.01'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M182.62 339.89 L178.32 342.24'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M190.36 344.12 L186.06 346.47'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <g transform='matrix(.86 .47 -.86 .47 211.00 285.40)'>
                <circle
                  cy='4'
                  r='46'
                  fill='#0A1420'
                  stroke='#33536B'
                  strokeWidth='2'
                />
                <circle
                  r='46'
                  fill='url(#hpe-disc)'
                  stroke='#6BC7F6'
                  strokeWidth='1.5'
                />
                <circle
                  r='14'
                  fill='none'
                  stroke='#6BC7F6'
                  opacity='.36'
                  strokeWidth='.8'
                />
                <circle
                  r='27'
                  fill='none'
                  stroke='#6BC7F6'
                  opacity='.36'
                  strokeWidth='.8'
                />
                <circle
                  r='39'
                  fill='none'
                  stroke='#6BC7F6'
                  opacity='.36'
                  strokeWidth='.8'
                />
                <path
                  d='M-45 0H45M0-45V45'
                  stroke='#6BC7F6'
                  strokeWidth='.8'
                  opacity='.3'
                />
                <g className='hpe-scanner'>
                  <path d='M0 0L0-44A44 44 0 0 1 44 0Z' fill='url(#hpe-scan)' />
                  <path d='M0 0L44 0' stroke='#C2EEFF' strokeWidth='1.5' />
                </g>
                <circle cx='-17' cy='-21' r='3' fill='#BEEDFF' />
                <circle
                  cx='-17'
                  cy='-21'
                  r='6'
                  fill='none'
                  stroke='#6BC7F6'
                  opacity='.35'
                />
                <circle cx='26' cy='12' r='3' fill='#BEEDFF' />
                <circle
                  cx='26'
                  cy='12'
                  r='6'
                  fill='none'
                  stroke='#6BC7F6'
                  opacity='.35'
                />
                <circle cx='-13' cy='27' r='3' fill='#BEEDFF' />
                <circle
                  cx='-13'
                  cy='27'
                  r='6'
                  fill='none'
                  stroke='#6BC7F6'
                  opacity='.35'
                />
                <circle r='4' fill='#E0F6FF' />
              </g>
              <path
                d='M207.56 263.48 L207.56 234.48'
                fill='none'
                stroke='#51B7EB'
                strokeWidth='0.8'
                opacity='.6'
              />
              <g transform='translate(207.56,227.48000000000002)'>
                <rect
                  x='-17'
                  y='-9'
                  width='34'
                  height='18'
                  rx='3'
                  fill='#132430'
                  stroke='#447893'
                />
                <path d='M-8 0h5m4 0h7' stroke='#AADEFA' strokeWidth='2' />
              </g>
            </a>
            <a
              className='hpe-module hpe-module-hive'
              data-module='hive'
              {...moduleEvents('hive')}
              href='/hive'
              tabIndex={0}
              aria-label='Explore Hive'
            >
              <g>
                <polygon
                  points='372.68,317.40 469.00,370.04 469.00,378.04 372.68,325.40'
                  fill='#10141E'
                  stroke='#384154'
                />
                <polygon
                  points='565.32,317.40 469.00,370.04 469.00,378.04 565.32,325.40'
                  fill='#1C2330'
                  stroke='#384154'
                />
                <polygon
                  points='469.00,264.76 565.32,317.40 469.00,370.04 372.68,317.40'
                  fill='#131925'
                  stroke='#384154'
                />
              </g>
              <g>
                <polygon
                  points='383.00,291.40 469.00,338.40 469.00,362.40 383.00,315.40'
                  fill='#191F2C'
                  stroke='#526078'
                />
                <polygon
                  points='555.00,291.40 469.00,338.40 469.00,362.40 555.00,315.40'
                  fill='#252C3C'
                  stroke='#526078'
                />
                <polygon
                  points='469.00,244.40 555.00,291.40 469.00,338.40 383.00,291.40'
                  fill='url(#hpe-surface)'
                  stroke='#526078'
                />
              </g>
              <path
                d='M383.00 310.40 L469.00 357.40 L555.00 310.40'
                fill='none'
                stroke='#F9CA45'
                strokeWidth='1.5'
                opacity='.85'
              />
              <path
                d='M537.80 316.80 L537.80 308.80'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M530.92 320.56 L530.92 312.56'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M524.04 324.32 L524.04 316.32'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M517.16 328.08 L517.16 320.08'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M510.28 331.84 L510.28 323.84'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M503.40 335.60 L503.40 327.60'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M496.52 339.36 L496.52 331.36'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M489.64 343.12 L489.64 335.12'
                fill='none'
                stroke='#5A6377'
                strokeWidth='1.2'
              />
              <path
                d='M401.92 318.74 L397.62 321.09'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M409.66 322.97 L405.36 325.32'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M417.40 327.20 L413.10 329.55'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M425.14 331.43 L420.84 333.78'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M432.88 335.66 L428.58 338.01'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M440.62 339.89 L436.32 342.24'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <path
                d='M448.36 344.12 L444.06 346.47'
                fill='none'
                stroke='#6F778A'
                strokeWidth='2'
              />
              <g className='hpe-hive-cell'>
                <polygon
                  points='451.80,253.90 469.00,263.30 469.00,275.30 451.80,265.90'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='486.20,253.90 469.00,263.30 469.00,275.30 486.20,265.90'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='469.00,244.50 486.20,253.90 469.00,263.30 451.80,253.90'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='473.30,258.65 490.50,268.05 490.50,287.05 473.30,277.65'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='507.70,258.65 490.50,268.05 490.50,287.05 507.70,277.65'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='490.50,249.25 507.70,258.65 490.50,268.05 473.30,258.65'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='494.80,277.40 512.00,286.80 512.00,298.80 494.80,289.40'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='529.20,277.40 512.00,286.80 512.00,298.80 529.20,289.40'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='512.00,268.00 529.20,277.40 512.00,286.80 494.80,277.40'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='430.30,258.65 447.50,268.05 447.50,287.05 430.30,277.65'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='464.70,258.65 447.50,268.05 447.50,287.05 464.70,277.65'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='447.50,249.25 464.70,258.65 447.50,268.05 430.30,258.65'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='451.80,257.40 469.00,266.80 469.00,298.80 451.80,289.40'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='486.20,257.40 469.00,266.80 469.00,298.80 486.20,289.40'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='469.00,248.00 486.20,257.40 469.00,266.80 451.80,257.40'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='473.30,282.15 490.50,291.55 490.50,310.55 473.30,301.15'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='507.70,282.15 490.50,291.55 490.50,310.55 507.70,301.15'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='490.50,272.75 507.70,282.15 490.50,291.55 473.30,282.15'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='408.80,277.40 426.00,286.80 426.00,298.80 408.80,289.40'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='443.20,277.40 426.00,286.80 426.00,298.80 443.20,289.40'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='426.00,268.00 443.20,277.40 426.00,286.80 408.80,277.40'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='430.30,282.15 447.50,291.55 447.50,310.55 430.30,301.15'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='464.70,282.15 447.50,291.55 447.50,310.55 464.70,301.15'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='447.50,272.75 464.70,282.15 447.50,291.55 430.30,282.15'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
              <g className='hpe-hive-cell'>
                <polygon
                  points='451.80,300.90 469.00,310.30 469.00,322.30 451.80,312.90'
                  fill='#9F7320'
                  stroke='#FFE395'
                />
                <polygon
                  points='486.20,300.90 469.00,310.30 469.00,322.30 486.20,312.90'
                  fill='#CA9428'
                  stroke='#FFE395'
                />
                <polygon
                  points='469.00,291.50 486.20,300.90 469.00,310.30 451.80,300.90'
                  fill='url(#hpe-goldGlass)'
                  stroke='#FFE395'
                />
              </g>
            </a>
            <g className='hpe-human-control'>
              <g>
                <polygon
                  points='284.96,382.90 340.00,412.98 340.00,425.98 284.96,395.90'
                  fill='#121825'
                  stroke='#647087'
                />
                <polygon
                  points='395.04,382.90 340.00,412.98 340.00,425.98 395.04,395.90'
                  fill='#2C3344'
                  stroke='#647087'
                />
                <polygon
                  points='340.00,352.82 395.04,382.90 340.00,412.98 284.96,382.90'
                  fill='#222838'
                  stroke='#647087'
                />
              </g>
              <g transform='matrix(.86 .47 -.86 .47 340.00 378.90)'>
                <rect
                  x='-25'
                  y='-25'
                  width='50'
                  height='50'
                  rx='10'
                  fill='#F7F2E7'
                />
              </g>
              <g transform='matrix(.86 .47 -.86 .47 340.00 378.90)'>
                <path
                  d='M-12 0l8 9 17-20'
                  transform='rotate(-45)'
                  stroke='#353335'
                  fill='none'
                  strokeWidth='3.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </g>
            </g>
          </g>
          <g className='hpe-art-label hpe-label-vector'>
            <path
              d='M341 154V120L371 103'
              fill='none'
              stroke='#515467'
              strokeWidth='1'
            />
            <circle cx='341' cy='154' r='2.5' fill='#EC368C' />
            <text x='382' y='91' fill='#F7F3EE' fontSize='18' fontWeight='550'>
              Vector
            </text>
            <text x='382' y='110' fill='#9798A8' fontSize='11.5'>
              Qualify the opportunity.
            </text>
          </g>
          <g className='hpe-art-label hpe-label-radar'>
            <path
              d='M178 289L123 257H53'
              fill='none'
              stroke='#515467'
              strokeWidth='1'
            />
            <circle cx='178' cy='289' r='2.5' fill='#51B7EB' />
            <text x='53' y='221' fill='#F7F3EE' fontSize='18' fontWeight='550'>
              Radar
            </text>
            <text x='53' y='240' fill='#9798A8' fontSize='11.5'>
              Find your signal.
            </text>
          </g>
          <g className='hpe-art-label hpe-label-hive'>
            <path
              d='M512 319L583 361V402L565 415'
              fill='none'
              stroke='#515467'
              strokeWidth='1'
            />
            <circle cx='512' cy='319' r='2.5' fill='#F9CA45' />
            <text x='525' y='436' fill='#F7F3EE' fontSize='18' fontWeight='550'>
              Hive
            </text>
            <text x='525' y='455' fill='#9798A8' fontSize='11.5'>
              Build the system.
            </text>
          </g>
          <g className='hpe-ownership'>
            <path
              d='M340 432V510'
              fill='none'
              stroke='#737A88'
              strokeWidth='1'
              strokeDasharray='3 4'
            />
            <rect
              x='238'
              y='518'
              width='204'
              height='34'
              rx='17'
              fill='#181A22'
              stroke='#4D4B45'
            />
            <circle cx='257' cy='535' r='3' fill='#F9CA45' />
            <text
              x='270'
              y='539.5'
              fontSize='12'
              fill='#EDEAE2'
              fontWeight='500'
            >
              Your direction. Your code.
            </text>
          </g>
        </g>
      </svg>
    </div>
  )
}
